import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Zap, Anchor, Car, Home, Sun, Battery } from "lucide-react";

const Maatwerk = () => {
  const maatwerkProjects = [
    {
      icon: <Anchor className="h-8 w-8 text-primary" />,
      title: "Boten & Jachten",
      description: "Off-grid energiesystemen voor uw boot. Van kleine zeiljachten tot luxe motojachten - wij zorgen voor betrouwbare stroom op het water.",
      features: ["Marine-grade componenten", "Zout water bestendig", "Compact en efficiënt", "12V en 24V systemen"]
    },
    {
      icon: <Car className="h-8 w-8 text-primary" />,
      title: "Campers & Caravans",
      description: "Geniet van volledige vrijheid tijdens uw reizen. Onze campersystemen geven u de energie die u nodig heeft, waar u ook bent.",
      features: ["Flexibele zonnepanelen", "Lithium batterijpakketten", "Slim energiebeheer", "Eenvoudige bediening"]
    },
    {
      icon: <Home className="h-8 w-8 text-primary" />,
      title: "Tuinhuizen & Chalets",
      description: "Maak uw tuinhuis, chalet of bijgebouw volledig energieneutraal. Perfect voor werkruimtes, hobbyruimtes of gastenoverblijven.",
      features: ["Off-grid oplossingen", "Verlichting & stopcontacten", "WiFi & entertainment", "Weersbestendige installatie"]
    },
    {
      icon: <Zap className="h-8 w-8 text-primary" />,
      title: "Mobiele Ondernemingen",
      description: "Voor foodtrucks, marktkramen en mobiele bedrijven. Betrouwbare energie voor uw onderneming, onafhankelijk van het elektriciteitsnet.",
      features: ["Hoge capaciteit systemen", "Snelle oplaadmogelijkheden", "Professionele installatie", "24/7 betrouwbaarheid"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Maatwerk Energie-oplossingen
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Geen standaard oplossing? Geen probleem! SOL-VI ontwerpt en installeert 
              op maat gemaakte zonne-energiesystemen voor elke unieke situatie. 
              Van boten tot campers, van tuinhuizen tot mobiele ondernemingen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                Bespreek uw Project
              </Button>
              <Button variant="outline" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                Vraag Offerte Aan
              </Button>
            </div>
          </div>
        </div>

        {/* Maatwerk Projects Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Onze Specialiteiten
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Van water tot land, van vakantie tot business - wij hebben de expertise 
                om uw energievraagstuk op te lossen.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {maatwerkProjects.map((project, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                        {project.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                        <p className="text-muted-foreground">{project.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {project.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0"></div>
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Onze Aanpak voor Maatwerk
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Elk project is uniek. Daarom hanteren wij een persoonlijke aanpak 
                voor elk maatwerkproject.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="font-semibold mb-2">Analyse & Advies</h3>
                <p className="text-sm text-muted-foreground">
                  We analyseren uw specifieke situatie en energiebehoefte
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="font-semibold mb-2">Custom Ontwerp</h3>
                <p className="text-sm text-muted-foreground">
                  Ontwerp van een op maat gemaakt systeem voor uw toepassing
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="font-semibold mb-2">Vakkundige Installatie</h3>
                <p className="text-sm text-muted-foreground">
                  Professionele installatie door onze ervaren specialisten
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  4
                </div>
                <h3 className="font-semibold mb-2">Support & Service</h3>
                <p className="text-sm text-muted-foreground">
                  Ondersteuning en service, ook na de installatie
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Showcase */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Van Concept tot Realiteit
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Met onze geavanceerde ontwerpsoftware brengen wij uw unieke energieproject 
                tot leven - van eerste schets tot gedetailleerde installatie.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="relative">
                <img 
                  src="/lovable-uploads/a9fc30ce-885e-4e51-bdad-7b9d59c74283.png" 
                  alt="Maatwerk solar design op verschillende apparaten"
                  className="rounded-lg shadow-lg w-full"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold">
                  Multi-platform Design
                </div>
              </div>
              <div className="relative">
                <img 
                  src="/lovable-uploads/3a9efa5b-bdfa-443f-9025-c94d958f47ca.png" 
                  alt="Thermische analyse voor optimaal ontwerp"
                  className="rounded-lg shadow-lg w-full"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold">
                  Thermische Analyse
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Waarom SOL-VI voor Maatwerk?
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Sun className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold">Technische Expertise</h3>
                      <p className="text-muted-foreground">Jarenlange ervaring met complexe, op maat gemaakte installaties</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Battery className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold">Kwaliteitscomponenten</h3>
                      <p className="text-muted-foreground">Uitsluitend A-merk componenten die bestand zijn tegen extreme omstandigheden</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold">Totaaloplossing</h3>
                      <p className="text-muted-foreground">Van ontwerp tot installatie en onderhoud - alles uit één hand</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-subtle p-8 rounded-lg">
                <h3 className="text-2xl font-bold mb-4">Start uw Maatwerkproject</h3>
                <p className="text-muted-foreground mb-6">
                  Heeft u een uniek energievraagstuk? Laat ons weten wat u voor ogen heeft. 
                  Wij denken graag met u mee om tot de perfecte oplossing te komen.
                </p>
                <div className="space-y-3">
                  <Button className="w-full" size="lg">
                    Plan een Brainstormsessie
                  </Button>
                  <Button variant="outline" className="w-full" size="lg">
                    Stuur ons uw Idee
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Maatwerk;